import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, ReceiptIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { CaseEntries } from '../../components/admin/case/CaseEntries';
import { StartTimerButton } from '../../components/admin/timer/StartTimerButton';
import { caseEntryTotals } from '../../utils/caseEntries';
import { formatKr } from '../../utils/quote';
import { defaultInvoiceBasis, useAdminData } from '../../contexts/AdminDataContext';
import type { InvoiceBasis } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages, priceModels } from '../../types/admin';
import type { CaseStatus, PhotoStage, PriceModel } from '../../types/admin';
import { validateImage } from '../../services/imageService';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase,
    invoiceForCase,
    createInvoiceFromCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  /** Valgte billeder der endnu ikke er uploadet — kan ses og fjernes først. */
  const [pending, setPending] = useState<{
    file: File;
    preview: string;
  }[]>([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [basisChoice, setBasisChoice] = useState<InvoiceBasis | null>(null);
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-940" data-mp="3texayjqz14q5" />;
  const customer = customerById(item.customerId);
  const invoice = invoiceForCase(item.id);
  const entryCount = item.entries?.length ?? 0;
  const registeredSubtotal = caseEntryTotals(item.entries ?? []).subtotal;
  const agreedAmount = Number(item.finalAmount || item.fixedPrice) || 0;
  // Valget vises kun, når der reelt er to grundlag at vælge imellem.
  const canChooseBasis = item.priceModel === 'Fast pris' && entryCount > 0;
  const invoiceBasis: InvoiceBasis = canChooseBasis ? basisChoice ?? defaultInvoiceBasis(item) : entryCount > 0 ? 'entries' : defaultInvoiceBasis(item);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-941" data-mp="2mrjj8hxij3bk">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-942" data-mp="2msbfluyxp52w">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-943" data-mp="z5f7bql0p1kv" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-944" data-mp="v5oj9umvkzk1">
        <div data-id="element-945" data-mp="q6243fnhl98l">
          <p className="text-sm text-ink-muted" data-id="element-946" data-mp="q6lca4nc4j9f">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-947" data-mp="1mvj87q6tmn86">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-948" data-mp="1is3d4yaeguxl">
            {customer?.name ?? item.customerName}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-949" data-mp="18wtcadvqmtle">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3" data-id="element-950" data-mp="3tlpr08rzmo4x">
          <StatusBadge status={item.status} data-id="element-951" data-mp="3tolo5uw0h8sb" />
          <StartTimerButton caseItem={item} data-id="element-952" data-mp="1lchyc25ggwot" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-953" data-mp="1lbgcr3qtcrsu">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-954" data-mp="292c7o9qkg34b" />
            Slet sag
          </button>
        </div>
      </header>

      {item.quoteId && <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-955" data-mp="201nufh8zy6sh">
          <p className="text-[15px] text-ink" data-id="element-956" data-mp="jmw1n37xuxin">
            Oprettet fra tilbud: <span className="font-medium" data-id="element-957" data-mp="oobkcomn8rxv">{item.quoteNumber}</span>
          </p>
          <Link to={`/admin/tilbud/${item.quoteId}`} className={buttonClasses('secondary', 'sm')} data-id="element-958" data-mp="h39e27tbtne0">
            Åbn tilbud
          </Link>
        </section>}

      {/* Faktura: oprettes fra sagen, typisk når arbejdet er færdigt. */}
      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-959" data-mp="q3xl2ikaynhe">
        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-960" data-mp="l6nw140tsdu3">
          <div data-id="element-961" data-mp="3tkef4droctwe">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-962" data-mp="3tlz9a2a2uatq">
              {invoice ? 'Faktura allerede oprettet' : 'Faktura'}
            </h2>
            <p className="mt-1 text-[15px] text-ink-soft" data-id="element-963" data-mp="75bkd4sp6ye0">
              {invoice ? `${invoice.invoiceNumber} · ${formatPrice(String(Math.round(invoice.total)))} · ${invoice.status}` : invoiceBasis === 'agreed' ? `Fakturaen udfyldes med den aftalte faste pris${item.quoteNumber ? ` fra ${item.quoteNumber}` : ''}. Alt kan rettes bagefter.` : entryCount > 0 ? `Fakturaen udfyldes med ${entryCount} registrerede linjer fra forbruget. Alt kan rettes bagefter.` : item.status === 'Færdig' ? 'Arbejdet er færdigt. Opret fakturaen ud fra sagen — alt kan rettes bagefter.' : 'Du kan oprette en fakturakladde, når du er klar. Alt kopieret kan rettes bagefter.'}
            </p>
          </div>
          {invoice ? <Link to={`/admin/fakturaer/${invoice.id}`} className={buttonClasses('secondary', 'md')} data-id="element-964" data-mp="qvfhp2fj7fgy">
              <ReceiptIcon className="h-4 w-4" aria-hidden="true" data-id="element-965" data-mp="10s0q7xqrf8pj" />
              Åbn faktura
            </Link> : <Button size="md" disabled={creatingInvoice} onClick={async () => {
          if (creatingInvoice) return;
          setCreatingInvoice(true);
          setInvoiceError('');
          const created = await createInvoiceFromCase(item, invoiceBasis);
          setCreatingInvoice(false);
          if (!created) {
            setInvoiceError('Fakturaen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/fakturaer/${created.id}`);
        }} data-id="element-966" data-mp="14uf3817qze8w">
              {creatingInvoice ? <>
                  <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-967" data-mp="3hzu0coabcsyi" />
                  Opretter …
                </> : 'Opret faktura'}
            </Button>}
        </div>
        {!invoice && canChooseBasis && <div className="mt-4 flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Fakturér ud fra" data-id="element-968" data-mp="340arft9ohdl5">
            <span className="mr-1 text-sm text-ink-muted" data-id="element-969" data-mp="177r5hc5rnown">Fakturér ud fra:</span>
            {([['agreed', `Aftalt fast pris · ${formatKr(agreedAmount)}`], ['entries', `Registreret forbrug · ${formatKr(registeredSubtotal)}`]] as [InvoiceBasis, string][]).map(([value, label]) => <button key={value} type="button" role="radio" aria-checked={invoiceBasis === value} onClick={() => setBasisChoice(value)} className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-150 ${invoiceBasis === value ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line text-ink-soft hover:border-ink/30'}`} data-id="element-970" data-mp="g4rf7eywuk9h">
                {label}
              </button>)}
          </div>}
        {invoiceError && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-971" data-mp="1ctz3uvutt9hm">
            {invoiceError}
          </p>}
      </section>

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-972" data-mp="1hr8z4s4wcfdd">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-973" data-mp="32aktqqbbktxb">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-974" data-mp="2xbrhf17rr8yq" />
        </div>
        <div className="mt-6" data-id="element-975" data-mp="3l2neoduzuwdd">
          <StatusTimeline status={item.status} data-id="element-976" data-mp="3g1pvbxy8zwaa" />
        </div>
      </section>

      <div className="mt-6" data-id="element-977" data-mp="3g3u3is36jhgd">
        <CaseEntries caseItem={item} data-id="element-978" data-mp="3uz9vmrk793a4" />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-979" data-mp="3uwnrz30fj7x6">
        <div className="space-y-5" data-id="element-980" data-mp="3q0ypaqt45awn">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-981" data-mp="3pxkovo40fbru">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-982" data-mp="20l8yqc87mqco">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-983" data-mp="1vn75wzkzfco0">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-984" data-mp="2h0j4vntazfcm">
              <div data-id="element-985" data-mp="2c16ljd1n4qes">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-986" data-mp="2c0n1nqsfjry4">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-987" data-mp="272ljd82tvwnp">{item.category}</dd>
              </div>
              <div data-id="element-988" data-mp="34pkgtl1ch8mn">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-989" data-mp="34osmedjhhgxy">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-990" data-mp="2zqhei8cr1gid">{item.address || '—'}</dd>
              </div>
              <div data-id="element-991" data-mp="2uz969fn5o16s">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-992" data-mp="2uyg4yu6yu3w1">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-993" data-mp="2q065i7e7kh4m">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-994" data-mp="3nielnm597kd5" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-995" data-mp="3ttbdlzmfv5x">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-996" data-mp="3vwacvir4yms">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-997" data-mp="8qjqo73lqmqa">
              <label htmlFor="ny-note" className="sr-only" data-id="element-998" data-mp="mmdo9tyxdpo3">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-999" data-mp="rkyp9vm04ddl" />
              <Button type="submit" variant="dark" data-id="element-1000" data-mp="ljram7t5dnp3">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-1001" data-mp="gqy4bfi167aw">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-1002" data-mp="uht6k8y0xq5i">
                  <div data-id="element-1003" data-mp="3696wcodiem7v">
                    <p className="text-xs text-ink-muted" data-id="element-1004" data-mp="368orx4gp11ov">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-1005" data-mp="31an82hbg0ycc">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-1006" data-mp="1grta3gvlh00u">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-1007" data-mp="1bzi4pyi34m0w" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-1008" data-mp="124hyxg3bri19">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1009" data-mp="t2h22kc06y04">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1010" data-mp="t4l40wqcdmxr">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1011" data-mp="2ihyc8x1krv45">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-1012" data-mp="2nfzu5en9loe1">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-1013" data-mp="nes6wsqpjwsn">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-1014" data-mp="ifyrgurv1vpu">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-1015" data-mp="3ht9910lj1epx">
                  {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1016" data-mp="1xaowrbjox1eb" /> : <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-1017" data-mp="22c4ht3lvb7wp" />}
                  {uploading ? 'Uploader …' : 'Vælg billeder'}
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
                  const input = event.target;
                  const files = Array.from(input.files ?? []);
                  input.value = '';
                  const problems = files.map(validateImage).filter(Boolean) as string[];
                  const accepted = files.filter(file => !validateImage(file));
                  setUploadError(problems.join(' '));
                  // Billederne lægges i kø, så de kan ses og fjernes før upload.
                  setPending(current => [...current, ...accepted.map(file => ({
                    file,
                    preview: URL.createObjectURL(file)
                  }))]);
                }} data-id="element-1018" data-mp="19n7atwn3wxj9" />
                </label>
              </div>
            </div>

            {pending.length > 0 && <div className="mt-5 rounded-xl border border-line bg-canvas p-4" data-id="element-1019" data-mp="1ackmhdvlctoz">
                <p className="text-sm text-ink-soft" data-id="element-1020" data-mp="1obakqwu5czex">
                  {pending.length} {pending.length === 1 ? 'billede' : 'billeder'} klar til {stage}
                </p>
                <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5" data-id="element-1021" data-mp="2zowllf9dcpga">
                  {pending.map((entry, index) => <li key={`${entry.file.name}-${index}`} className="relative" data-id="element-1022" data-mp="2vnjdeyit0a67" data-mpi={index}>
                      <img src={entry.preview} alt={entry.file.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-1023" data-mp="336k7yubmmthf" />
                      <button type="button" onClick={() => {
                  URL.revokeObjectURL(entry.preview);
                  setPending(current => current.filter((_, i) => i !== index));
                }} aria-label={`Fjern ${entry.file.name}`} className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-1024" data-mp="2tb8tmd44roa7">
                        <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1025" data-mp="8qiboxyenumt" />
                      </button>
                    </li>)}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-1026" data-mp="29l69sca4sb2a">
                  <Button size="sm" disabled={uploading} onClick={async () => {
                if (uploading) return;
                setUploading(true);
                setUploadError('');
                const result = await addPhotos(item.id, pending.map(entry => entry.file), stage);
                setUploading(false);
                if (!result.ok) {
                  // Køen bevares, så uploaden kan forsøges igen.
                  setUploadError(result.error ?? 'Billederne kunne ikke uploades.');
                  return;
                }
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
              }} data-id="element-1027" data-mp="29mprm7bfqu66">
                    {uploading ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1028" data-mp="t38n7tb9fx5t" />
                        Uploader …
                      </> : uploadError ? 'Prøv upload igen' : `Upload til ${stage}`}
                  </Button>
                  <button type="button" disabled={uploading} onClick={() => {
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
                setUploadError('');
              }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-50" data-id="element-1029" data-mp="1bsf02zpdhujp">
                    Ryd
                  </button>
                </div>
              </div>}

            {uploadError && <p role="alert" className="mt-4 text-sm font-medium text-danger" data-id="element-1030" data-mp="24ga7z3oiv1mc">
                {uploadError}
              </p>}

            <div className="mt-5 space-y-6" data-id="element-1031" data-mp="29ek3jfe5s48h">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-1032" data-mp="2j5es4qevnfx3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1033" data-mp="2o4ixt72levio">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-1034" data-mp="2s6w6o4cmz9jo">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-1035" data-mp="xsd1tv4mgtyh">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-1036" data-mp="xu6a1euvvm8l" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-1037" data-mp="qgx90p2aa473">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1038" data-mp="195vfmmes3uir" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-1039" data-mp="3ozc9v6iimuww">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-1040" data-mp="3me4ugt8z4fy3">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1041" data-mp="3mf66azxtd6gv">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1042" data-mp="3rc6dxfabaha3">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-1043" data-mp="3cffamuxiolf7">
              {customer?.name ?? item.customerName}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-1044" data-mp="33nwu2iwns3ll">
              <li data-id="element-1045" data-mp="33ltw9cp9l66b">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-1046" data-mp="38evffwwi7rvp">{customer?.email || '—'}</li>
              <li data-id="element-1047" data-mp="2tpqwgdhvxjmj">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-1048" data-mp="2traea8j6w2qf">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-1049" data-mp="2yo0ucvuzuybu">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1050" data-mp="2kosojk4bsryo" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-1051" data-mp="2opl2gqo57l1a">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1052" data-mp="263dkdzpjl8v8" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-1053" data-mp="2f2s9ovtz3de5">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1054" data-mp="2oyv6rn9ywlu8">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1055" data-mp="2k1v0pcd4bj34">Økonomi</h2>
            <SelectField id="sag-prisform" label="Prisform" className="mt-4" hint={item.priceModel === 'Fast pris' ? 'Faktureres som aftalt fast pris.' : 'Faktureres ud fra registreret forbrug.'} value={item.priceModel} onChange={event => updateCase(item.id, {
            priceModel: event.target.value as PriceModel
          })} options={priceModels.map(option => ({
            value: option,
            label: option
          }))} data-id="element-1056" data-mp="2xsqcawf04eae" />
            <dl className="mt-2 divide-y divide-line/70 text-[15px]" data-id="element-1057" data-mp="3quhs7clxssup">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1058" data-mp="3vo2i0hddmgit">
                <dt className="text-ink-soft" data-id="element-1059" data-mp="4ggjf35il48t">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-1060" data-mp="4fycncl877mn">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1061" data-mp="33y7pycy4wcqg">
                <dt className="text-ink-soft" data-id="element-1062" data-mp="37xyox3xtdz62">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-1063" data-mp="3cw061jbojoew">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1064" data-mp="3hvwbln00znwk">
                <dt className="text-ink-soft" data-id="element-1065" data-mp="s6atxe2d2gpw">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-1066" data-mp="s5snjolzim49">{formatPrice(item.estimate)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1067" data-mp="x3kfujqjka8z">
                <dt className="text-ink-soft" data-id="element-1068" data-mp="2hnpnu1qfick1">Registreret forbrug</dt>
                <dd className="font-medium text-ink" data-id="element-1069" data-mp="2comq72cs38m3">
                  {formatKr(caseEntryTotals(item.entries ?? []).subtotal)}
                  <span className="ml-1 text-xs font-normal text-ink-muted" data-id="element-1070" data-mp="27wl5cs1jo9kg">ekskl. moms</span>
                </dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-1071" data-mp="22wpd1q27m6kq" />
          </section>
        </aside>
      </div>
    </div>;
}